import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  Gamepad2, Gift, Mountain, Users, Trophy, Headphones, Globe, 
  ChevronDown, ChevronRight, LayoutGrid, Heart, Flame, Compass, Spade,
  Bookmark, ArrowRightCircle, Star, FileText, ShieldCheck, BookOpen, MessageSquare,
  Facebook, Twitter, Instagram, Youtube, Send, X
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useActiveGroups } from "hooks/useGameEngine";
import { useSiteSettingsQuery } from "hooks/useAuth";
import { addNewStyle, API, CRISTP_ID, wait, Event } from 'utils/Helper';
import storage from "utils/Storage";

import styled from "styled-components";

const closeMobileSidebar = () => {
  // Close mobile sidebar on nav link click (only on mobile/tablet view)
  if (window.innerWidth >= 1024) return;
  const aside = document.querySelector('aside');
  if (aside) aside.style.transform = 'translateX(-100%)';
  Event.emit("closeMobileSidebar");
};

const StyledSidebarItem = styled.div`
  position: relative !important;
  display: flex !important;
  align-items: center !important;
  gap: 12px !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  user-select: none !important;
  overflow: hidden !important;
  transition: all 0.2s ease-in-out !important;
  text-decoration: none !important;
  border: none !important;
  background: none;

  ${props => props.collapsed ? `
    width: 40px !important;
    height: 40px !important;
    justify-content: center !important;
    padding: 0 !important;
    margin: 0 auto !important;
  ` : `
    width: 100% !important;
    padding: 10px 16px !important;
  `}

  ${props => props.disabled ? `
    opacity: 0.4 !important;
    color: #555c63 !important;
    cursor: not-allowed !important;
  ` : props.active ? `
    background-color: #2f4553 !important;
    color: #ffffff !important;
  ` : `
    color: #8e969f !important;
    cursor: pointer !important;
    &:hover {
      background-color: #213743 !important;
      color: #ffffff !important;
    }
  `}

  svg {
    flex-shrink: 0;
  }
`;

const SidebarItem = ({ icon: Icon, label, to, onClick, collapsed, active, disabled, hasChevron }) => {
  const renderIcon = () => {
    if (typeof Icon === 'string') {
      return <img src={Icon} alt={label} className="shrink-0" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />;
    }
    const IconComponent = Icon || Trophy;
    return <IconComponent size={18} strokeWidth={2} className="shrink-0" />;
  };

  const content = (
    <>
      {renderIcon()}
      {/* Text hidden instantly when collapsing */}
      {!collapsed && (
        <span className="flex-1 text-left whitespace-nowrap overflow-hidden" style={{ marginLeft: '12px' }}>
          {label}
        </span>
      )}
      {hasChevron && !collapsed && <ChevronRight size={14} className="text-gray-500 ml-auto shrink-0" />}
      {/* Tooltip on hover when collapsed */}
      {collapsed && (
        <span className="absolute left-14 top-1/2 -translate-y-1/2 scale-0 group-hover:scale-100 bg-[#0f212e] border border-gray-700 text-white text-[11px] font-bold px-2 py-1 rounded-md shadow-xl transition-all duration-100 z-50 whitespace-nowrap pointer-events-none">
          {label}{disabled ? " (Sign in required)" : ""}
        </span>
      )}
    </>
  );

  if (disabled) return <StyledSidebarItem as="div" title="Sign in required" collapsed={collapsed} disabled={disabled}>{content}</StyledSidebarItem>;
  if (to) return <StyledSidebarItem as={Link} to={to} collapsed={collapsed} active={active} onClick={closeMobileSidebar}>{content}</StyledSidebarItem>;
  return <StyledSidebarItem as="button" onClick={onClick} collapsed={collapsed} active={active} style={{ textAlign: 'left' }}>{content}</StyledSidebarItem>;
};

export function Sidebar({ isCollapsed, setIsCollapsed, isCollapsedMobile, setIsCollapsedMobile, isSmallScreen }) {
  console.log("STAKE SIDEBAR RENDERED");
  const location = useLocation();
  const path = location.pathname;
  const { data: groupsData } = useActiveGroups();
  const groups = groupsData || [];
  
  const { data: settingsData } = useSiteSettingsQuery();
  const settings = settingsData?.data?.[0] || {};

  const [loadedCrisp, setLoadedCrisp] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);

  // Initialize Crisp support chat
  const handleSupport = () => {
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = CRISTP_ID;

    if (!loadedCrisp) {
      setLoadedCrisp(true);
      const script = document.createElement("script");
      script.src = "https://client.crisp.chat/l.js";
      script.id = "chtt";
      script.async = true;
      document.head.appendChild(script);
    }

    addNewStyle("#crisp-chatbox {display:block !important;}");
    addNewStyle(".crisp-client .cc-kv6t .cc-1xry .cc-ge4v .cc-qqzz .cc-5b24 .cc-19cg {display:none !important;}");

    wait(2000).then(() => {
      try {
        window.$crisp.push(["do", "chat:open"]);
      } catch (e) {
        console.error(e);
      }
    });
  };

  const isAuthed = storage.getKey("logged") ? true : false;

  // Determine current active mode
  const isCasino = path.startsWith('/casino') || path.startsWith('/group');
  const isSports = path.startsWith('/sports');
  const isHomePage = path === "/";
  const currentMode = isSports ? "sports" : (isCasino ? "casino" : "home");

  // User items at the top of the sidebar (Only shown if logged in)
  const userItems = [
    { key: "saved-games", label: "Saved Games", icon: Bookmark, to: `/group/saved-games` },
    { key: "continue-playing", label: "Continue Playing", icon: ArrowRightCircle, to: `/group/continue-playing` },
    { key: "games-for-you", label: "Games For You", icon: Star, to: `/group/games-for-you` },
    { key: "my-bets", label: "My Bets", icon: FileText, to: `/my-bets` }
  ];

  // Resolve dynamic prefix and groups filtering
  let dynamicGroupPrefix = '/group/';
  let filteredGroups = [];
  if (currentMode === "sports") {
    dynamicGroupPrefix = '/sports/group/';
    filteredGroups = groups.filter(g => g.show_as_tab_sports);
  } else if (currentMode === "casino") {
    dynamicGroupPrefix = '/casino/group/';
    filteredGroups = groups.filter(g => g.show_as_tab_casino);
  } else {
    dynamicGroupPrefix = '/group/';
    filteredGroups = groups.filter(g => g.show_on_home_index);
  }

  // Static sports categories
  const sportsCategories = [
    { key: "soccer", label: "Soccer", icon: Trophy, to: "/sports" },
    { key: "basketball", label: "Basketball", icon: Trophy, to: "/sports" },
    { key: "tennis", label: "Tennis", icon: Trophy, to: "/sports" },
    { key: "cricket", label: "Cricket", icon: Trophy, to: "/sports" }
  ];

  return (
    <>
      {/* Mobile Backdrop */}
      {isCollapsedMobile && (
        <div 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (setIsCollapsedMobile) setIsCollapsedMobile(false);
            Event.emit("closeMobileSidebar");
            // Direct DOM fallback
            const aside = document.querySelector('aside');
            if (aside) aside.style.transform = 'translateX(-100%)';
            const backdrop = e.currentTarget;
            if (backdrop) backdrop.style.display = 'none';
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
            zIndex: 9998,
          }}
          className="lg:hidden"
        />
      )}

      <aside 
        style={isSmallScreen ? {
          backgroundColor: "#0F1E25",
          borderRight: "1px solid #2f4553",
          position: "fixed",
          left: 0,
          top: 0,
          height: "100%",
          width: "85%",
          maxWidth: "340px",
          zIndex: 9999,
          transform: isCollapsedMobile ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.3s ease",
        } : {
          backgroundColor: "#0F1E25",
          borderRight: "1px solid #2f4553",
          height: "calc(100vh - 72px)",
          position: "fixed",
          left: 0,
          top: "72px",
          width: isCollapsed ? "64px" : "244px",
          zIndex: 30,
          transition: "width 0.3s ease, transform 0.3s ease",
        }}
        className="flex flex-col shrink-0 overflow-y-auto py-4"
      >
        {/* Mobile Header with logo & fold button */}
        <div className="lg:hidden flex items-center justify-between px-4 pb-4 border-b border-gray-800/80 mb-2">
          <img 
            src={window?.siteConfig?.logo_big ? (API + window.siteConfig.logo_big) : "/assets/images/logo.png"} 
            alt="Logo" 
            className="h-8 max-w-[130px] object-contain"
            onError={(e) => {
              e.target.src = "/assets/images/logo.png";
            }}
          />
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (setIsCollapsedMobile) setIsCollapsedMobile(false);
              Event.emit("closeMobileSidebar");
              // Direct DOM fallback
              const aside = document.querySelector('aside');
              if (aside) aside.style.transform = 'translateX(-100%)';
              const backdrop = document.querySelector('.lg\\:hidden.fixed.inset-0.bg-black\\/60');
              if (backdrop) backdrop.style.display = 'none';
            }}
            className="w-10 h-10 grid place-items-center rounded-md hover:bg-accent/50 text-gray-400 hover:text-white"
            style={{ border: "none", background: "none", cursor: "pointer", zIndex: 100000, position: "relative" }}
          >
            <X size={24} strokeWidth={2.5} />
          </button>
        </div>
      {/* Collapsed Shortcut Buttons (Casino & Sports) */}
      {isCollapsed && (
        <div className="px-2 space-y-2 mb-2">
          <Link 
            to="/casino" 
            title="Casino" 
            className={`w-10 h-10 mx-auto grid place-items-center rounded-lg text-white ${isCasino ? "opacity-100" : "opacity-50 hover:opacity-100"}`} 
            style={{ background: "linear-gradient(180deg,#1fc16b,#0f8a47)" }}
          >
            <Spade className="w-5 h-5 shrink-0" />
          </Link>
          <Link 
            to="/sports" 
            title="Sports" 
            className={`w-10 h-10 mx-auto grid place-items-center rounded-lg text-white ${isSports ? "opacity-100" : "opacity-50 hover:opacity-100"}`} 
            style={{ background: "linear-gradient(135deg,#ff7a1a 0%,#d94c0a 100%)" }}
          >
            <Trophy className="w-5 h-5 shrink-0" />
          </Link>
          <div className="my-2 mx-3 border-t border-gray-800/80" />
        </div>
      )}

      {/* Main Navigation List */}
      <div className={isCollapsed ? "px-2 space-y-1" : "px-3.5 space-y-1"}>
        
        {/* 1. TOP SECTION: 5 User Items */}
        {userItems.map(item => (
          <SidebarItem 
            key={item.key}
            icon={item.icon}
            label={item.label}
            to={item.to}
            collapsed={isCollapsed}
            active={path === item.to}
            disabled={!isAuthed}
          />
        ))}
        <div style={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", margin: "12px 0", height: "0px", width: "100%" }} />

        {/* 2. DYNAMIC CONTENT SECTION */}
        {currentMode === "sports" ? (
          <>
            <div 
              className="overflow-hidden transition-all duration-300"
              style={{ 
                maxHeight: isCollapsed ? "0px" : "40px",
                opacity: isCollapsed ? 0 : 1
              }}
            >
              <div className="px-4 pt-1 pb-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                Top Sports
              </div>
            </div>

            {filteredGroups.map((group) => {
              const groupName = group.name || group.group_name || "";
              let IconComponent = Trophy;
              
              if (group.icon) {
                if (LucideIcons[group.icon]) {
                  IconComponent = LucideIcons[group.icon];
                } else {
                  IconComponent = group.icon.startsWith('/uploads/') ? (API + group.icon) : group.icon;
                }
              } else {
                if (groupName.toLowerCase().includes("soccer")) IconComponent = Trophy;
              }

              const targetLink = `${dynamicGroupPrefix}${group.slug || group.id}`;

              return (
                <SidebarItem 
                  key={group.id}
                  icon={IconComponent}
                  label={groupName}
                  to={targetLink}
                  collapsed={isCollapsed}
                  active={path === targetLink}
                />
              );
            })}
          </>
        ) : (
          <>
            <div 
              className="overflow-hidden transition-all duration-300"
              style={{ 
                maxHeight: isCollapsed ? "0px" : "40px",
                opacity: isCollapsed ? 0 : 1
              }}
            >
              <div className="px-4 pt-1 pb-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                Games
              </div>
            </div>

            {filteredGroups.map((group) => {
              const groupName = group.name || group.group_name || "";
              let IconComponent = Gamepad2;
              
              if (group.icon) {
                if (LucideIcons[group.icon]) {
                  IconComponent = LucideIcons[group.icon];
                } else {
                  IconComponent = group.icon.startsWith('/uploads/') ? (API + group.icon) : group.icon;
                }
              } else {
                if (groupName.toLowerCase().includes("popular")) IconComponent = Flame;
                if (groupName.toLowerCase().includes("originals")) IconComponent = Spade;
                if (groupName.toLowerCase().includes("slots")) IconComponent = Spade;
                if (groupName.toLowerCase().includes("live")) IconComponent = LayoutGrid;
              }

              const targetLink = `${dynamicGroupPrefix}${group.slug || group.id}`;

              return (
                <SidebarItem 
                  key={group.id}
                  icon={IconComponent}
                  label={groupName}
                  to={targetLink}
                  collapsed={isCollapsed}
                  active={path === targetLink}
                />
              );
            })}
          </>
        )}

        {/* Separator */}
        <div style={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", margin: "12px 0", height: "0px", width: "100%" }} />

        {/* 3. COMMON UTILITIES SECTION */}
        <SidebarItem 
          icon={Gift} 
          label="Promotions" 
          to="/promotions" 
          collapsed={isCollapsed} 
          active={path === "/promotions"} 
        />

        <SidebarItem 
          icon={Compass} 
          label="Missions" 
          to="/missions" 
          collapsed={isCollapsed} 
          active={path === "/missions"} 
        />

        <SidebarItem 
          icon={Flame} 
          label="Offers" 
          to="/offers" 
          collapsed={isCollapsed} 
          active={path === "/offers"} 
        />

        <SidebarItem 
          icon={Users} 
          label="Affiliate" 
          to="/affiliate" 
          collapsed={isCollapsed} 
          active={path.startsWith("/affiliate")} 
        />

        <SidebarItem 
          icon={Trophy} 
          label="VIP Club" 
          to="/vip-club" 
          collapsed={isCollapsed} 
          active={path === "/vip-club"} 
        />



        {/* Separator */}
        <div style={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", margin: "12px 0", height: "0px", width: "100%" }} />

        {/* 4. FOOTER UTILITIES SECTION */}
        <SidebarItem 
          icon={Headphones} 
          label="Live Support" 
          onClick={() => {
            if (isCollapsed && setIsCollapsed) setIsCollapsed(false);
            setIsSupportOpen(!isSupportOpen);
          }} 
          collapsed={isCollapsed} 
          hasChevron={!isCollapsed}
        />

        {/* Live Support Sub-menu */}
        {!isCollapsed && isSupportOpen && (
          <div className="ml-6 pl-4 border-l-2 border-white/5 space-y-1 mt-1 mb-3">
            {(settings?.social_links || [])
              .filter(link => link.is_enabled)
              .map(link => {
                const isLiveChat = link.type === 'live_chat';
                
                let IconComp = Headphones;
                if (link.icon_name === 'Telegram') IconComp = Send;
                else if (link.icon_name === 'Facebook') IconComp = Facebook;
                else if (link.icon_name === 'Twitter') IconComp = Twitter;
                else if (link.icon_name === 'Instagram') IconComp = Instagram;
                else if (link.icon_name === 'Youtube') IconComp = Youtube;

                return (
                  <a
                    key={link.id}
                    href={isLiveChat ? '#' : (link.url || '#')}
                    target={isLiveChat ? '_self' : '_blank'}
                    rel="noreferrer"
                    onClick={isLiveChat ? (e) => { e.preventDefault(); handleSupport(); } : undefined}
                    className="flex items-center gap-3 py-2 px-3 text-sm font-semibold text-[#8e969f] hover:text-white hover:bg-[#213743] rounded-md transition-colors"
                  >
                    {link.icon_url ? (
                      <img src={link.icon_url.startsWith('http') ? link.icon_url : `${API}/uploads/logos/${link.icon_url}`} alt={link.name} className="w-4 h-4 object-contain" />
                    ) : (
                      <IconComp size={16} />
                    )}
                    <span>{link.name}</span>
                  </a>
                );
            })}
          </div>
        )}

        <SidebarItem 
          icon={ShieldCheck} 
          label="Provably Fair" 
          to="/provably-fair" 
          collapsed={isCollapsed} 
          active={path === "/provably-fair"} 
        />

        <SidebarItem 
          icon={Globe} 
          label="Language: English" 
          collapsed={isCollapsed} 
          hasChevron={!isCollapsed} 
        />
      </div>
    </aside>
    </>
  );
}

export default Sidebar;
